// ═══════════════════════════════════════════════════════════════════════════════
// PAGE - Gérer vos Formules (Cockpit Pro)
// Charte graphique : Orange (#F97316), Vert Fondateur (#22C55E), Navy (#1E3A5F)
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Crown, 
  Building2, 
  Sparkles, 
  Check, 
  ArrowLeft,
  Wand2,
  Bot,
  Globe,
  ShoppingBag,
  Share2,
  Headphones,
  ChevronDown,
  Clock,
  Zap,
  Gift,
  Percent,
  Users,
  Video,
  Rocket,
  Box,
  Megaphone,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Données des formules
const FORMULAS = [
  {
    id: 'pro',
    name: 'Pro',
    icon: Crown,
    priceMonthly: 50,
    priceYearly: 25,
    setupPrice: 500,
    setupPriceFounder: 250,
    abracaCoins: 5000,
    features: [
      'Assistant IA illimité',
      '5 000 AbracadaCoin/mois',
      'Boutique virtuelle',
      '1 collaborateur',
      'Support email',
    ]
  },
  {
    id: 'business',
    name: 'Business',
    icon: Building2,
    priceMonthly: 100,
    priceYearly: 50,
    setupPrice: 1000,
    setupPriceFounder: 500,
    abracaCoins: 15000,
    popular: true,
    features: [
      'Tout Pro +',
      '15 000 AbracadaCoin/mois',
      'Multi-boutiques (3)',
      '5 collaborateurs',
      'Support prioritaire',
      'Nom de domaine personnalisé',
    ]
  },
  {
    id: 'premium',
    name: 'Premium',
    icon: Sparkles,
    priceMonthly: 150,
    priceYearly: 75,
    setupPrice: 1500,
    setupPriceFounder: 750,
    abracaCoins: 50000,
    features: [
      'Tout Business +',
      '50 000 AbracadaCoin/mois',
      'Boutiques illimitées',
      'Collaborateurs illimités',
      'Support dédié 24/7',
      'API personnalisée',
      'Formation incluse',
    ]
  }
];

export default function FormulesPage() {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [currentFormula, setCurrentFormula] = useState('pro');
  const [showDetails, setShowDetails] = useState(false);
  
  const isYearly = billingPeriod === 'yearly';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-6 relative">
      {/* Croix en haut à droite */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
      >
        <X size={20} className="text-gray-500" />
      </button>
      
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#1E3A5F] hover:text-[#F97316] mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Retour</span>
          </button>
          
          <h1 className="text-3xl font-bold text-[#1E3A5F] text-center">Gérer vos formules</h1>
        </div>
        
        {/* Offre Fondateur - Design Premium */}
        {isYearly && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 relative mt-5"
          >
            {/* Badge "Offre Fondateur" à cheval sur la bordure */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-20">
              <span 
                className="px-5 py-2 rounded-full bg-[#22C55E] text-white text-sm font-semibold whitespace-nowrap"
                style={{ boxShadow: '0 4px 14px rgba(34, 197, 94, 0.35)' }}
              >
                Offre Fondateur
              </span>
            </div>
            
            {/* Conteneur principal - fond navy premium */}
            <div 
              className="relative rounded-2xl border border-[#22C55E]/30 overflow-hidden"
              style={{
                background: 'linear-gradient(145deg, #0F2744 0%, #1E3A5F 50%, #162d4a 100%)',
                boxShadow: '0 8px 32px rgba(15, 39, 68, 0.4)'
              }}
            >
              <div className="relative z-10 pt-9 pb-5 px-4">
                {/* Contenu centré */}
                <div className="flex flex-col items-center text-center">
                  {/* Sous-titre */}
                  <p className="text-white/60 text-xs tracking-wide mb-5">
                    Réservée aux 100 premiers inscrits
                  </p>
                  
                  {/* 3 cartes avantages */}
                  <div className="flex justify-center gap-3 mb-5 w-full max-w-sm">
                    {/* Carte 1 : -50% Aménagement */}
                    <div 
                      className="flex-1 rounded-xl flex flex-col items-center justify-center py-3 px-2"
                      style={{ 
                        background: 'rgba(34, 197, 94, 0.12)',
                        border: '1px solid rgba(34, 197, 94, 0.25)'
                      }}
                    >
                      <span className="text-[#4ade80] font-bold text-xl leading-none">-50%</span>
                      <span className="text-white/70 text-[10px] mt-1.5 leading-tight">Aménagement</span>
                    </div>
                    
                    {/* Carte 2 : -50% Loyer à vie */}
                    <div 
                      className="flex-1 rounded-xl flex flex-col items-center justify-center py-3 px-2"
                      style={{ 
                        background: 'rgba(34, 197, 94, 0.12)',
                        border: '1px solid rgba(34, 197, 94, 0.25)'
                      }}
                    >
                      <span className="text-[#4ade80] font-bold text-xl leading-none">-50%</span>
                      <span className="text-white/70 text-[10px] mt-1.5 leading-tight">Loyer à vie</span>
                    </div>
                    
                    {/* Carte 3 : + D'avantages */}
                    <div 
                      className="flex-1 rounded-xl flex flex-col items-center justify-center py-3 px-2"
                      style={{ 
                        background: 'rgba(34, 197, 94, 0.12)',
                        border: '1px solid rgba(34, 197, 94, 0.25)'
                      }}
                    >
                      <span className="text-[#4ade80] font-bold text-xl leading-none">+</span>
                      <span className="text-white/70 text-[10px] mt-1.5 leading-tight text-center">D'avantages</span>
                    </div>
                  </div>
                  
                  {/* Bouton "Voir les détails de l'offre" - capsule premium */}
                  <motion.button
                    onClick={() => setShowDetails(!showDetails)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-white/90 text-xs font-medium transition-all"
                    style={{
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    {showDetails ? 'Masquer les détails' : 'Voir les détails de l\'offre'}
                    <motion.div
                      animate={{ rotate: showDetails ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </motion.div>
                  </motion.button>
                </div>
                
                {/* Détails - visible au clic */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="mt-5 pt-5 border-t border-white/10">
                        {/* Section Aménagement clé en main */}
                        <div className="mb-5">
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                              <Wand2 className="w-4 h-4 text-[#F97316]" />
                              Aménagement clé en main
                            </h4>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#F97316] text-white">
                              -50%
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/10 text-white/80 flex items-center gap-1">
                              <Clock className="w-2.5 h-2.5" />
                              Livré sous 7 jours
                            </span>
                          </div>
                          
                          <p className="text-white/60 text-xs mb-3">
                            On s'occupe de tout — tu n'as rien à faire
                          </p>
                          
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { icon: Bot, text: 'Configuration agents IA' },
                              { icon: Globe, text: 'Création boutique & site' },
                              { icon: ShoppingBag, text: 'Import produits' },
                              { icon: Sparkles, text: 'Personnalisation' },
                              { icon: Share2, text: 'Réseaux sociaux' },
                              { icon: Headphones, text: 'Support 30 jours' },
                            ].map((item, i) => (
                              <div key={i} className="flex items-center gap-2 text-white/80">
                                <div className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0">
                                  <item.icon className="w-3 h-3 text-[#F97316]" />
                                </div>
                                <span className="text-[11px]">{item.text}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Section Bonus Fondateurs */}
                        <div className="pt-5 border-t border-white/10">
                          <h4 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                            <Gift className="w-4 h-4 text-[#4ade80]" />
                            Bonus Fondateurs exclusifs
                          </h4>
                          
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { icon: Globe, title: 'Domaine offert 5 ans', color: 'text-[#22C55E]', bg: 'bg-[#22C55E]/15' },
                              { icon: Percent, title: '-50% aménagement', color: 'text-[#F97316]', bg: 'bg-[#F97316]/15' },
                              { icon: Sparkles, title: '-50% loyer à vie', color: 'text-[#4ade80]', bg: 'bg-[#4ade80]/15' },
                              { icon: Crown, title: 'Badge Fondateur', color: 'text-amber-400', bg: 'bg-amber-400/15' },
                              { icon: Megaphone, title: 'Mise en avant réseaux', color: 'text-sky-400', bg: 'bg-sky-400/15' },
                              { icon: Video, title: 'Webinaire trimestriel', color: 'text-sky-400', bg: 'bg-sky-400/15' },
                              { icon: Rocket, title: 'Accès avant-première', color: 'text-pink-400', bg: 'bg-pink-400/15' },
                              { icon: Box, title: '-50% boutique 3D', color: 'text-purple-400', bg: 'bg-purple-400/15' },
                            ].map((item, i) => (
                              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.03]">
                                <div className={`w-6 h-6 rounded-md ${item.bg} flex items-center justify-center flex-shrink-0`}>
                                  <item.icon className={`w-3 h-3 ${item.color}`} />
                                </div>
                                <span className="text-white/70 text-[10px] leading-tight">{item.title}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Footer */}
                        <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-center gap-2 text-[#4ade80]">
                          <Zap className="w-3.5 h-3.5" />
                          <span className="text-[11px] font-medium">Places limitées — Sécurise ton avantage fondateur</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Cards des formules - Design sombre avec toggle individuel */}
        <div className="grid md:grid-cols-3 gap-6">
          {FORMULAS.map((formula, index) => {
            const Icon = formula.icon;
            const isCurrentPlan = currentFormula === formula.id;
            const savingsMonthly = (formula.priceMonthly - formula.priceYearly) * 12;
            const savingsSetup = formula.setupPrice - formula.setupPriceFounder;
            
            return (
              <motion.div
                key={formula.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-2xl p-6 ${
                  formula.popular 
                    ? 'bg-[#FFF7ED] border-2 border-[#F97316]' 
                    : 'bg-white border-2 border-[#1E3A5F]'
                }`}
              >
                {/* Badge Populaire - Orange */}
                {formula.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Le plus populaire
                    </span>
                  </div>
                )}
                
                {/* Toggle Annuel dans chaque carte */}
                <div className="flex items-center gap-2 mb-5 p-2 rounded-lg bg-gray-100">
                  <button
                    onClick={() => setBillingPeriod(billingPeriod === 'yearly' ? 'monthly' : 'yearly')}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      isYearly ? 'bg-[#22C55E]' : 'bg-gray-400'
                    }`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                      isYearly ? 'left-5' : 'left-0.5'
                    }`} />
                  </button>
                  <span className="text-[#1E3A5F] text-xs">
                    Passer à l'annuel et <span className="text-[#22C55E] font-semibold">économise {savingsMonthly}€</span>
                  </span>
                </div>
                
                {/* Header */}
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-[#1E3A5F]">{formula.name}</h3>
                  <p className="text-gray-500 text-sm mt-1">
                    {formula.id === 'pro' && 'Parfait pour démarrer'}
                    {formula.id === 'business' && 'Pour les entrepreneurs ambitieux'}
                    {formula.id === 'premium' && 'Pour les entreprises établies'}
                  </p>
                </div>
                
                {/* Prix */}
                <div className="mb-5">
                  <div className="flex items-baseline gap-2">
                    {isYearly && (
                      <span className="text-lg text-gray-400 line-through">
                        {formula.priceMonthly}€
                      </span>
                    )}
                    <span className="text-4xl font-bold text-[#1E3A5F]">
                      {isYearly ? formula.priceYearly : formula.priceMonthly}€
                    </span>
                    <span className="text-gray-500">/mois</span>
                  </div>
                  {isYearly && (
                    <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold bg-[#22C55E]/20 text-[#22C55E]">
                      Économise {savingsMonthly}€/an
                    </span>
                  )}
                </div>
                
                {/* AbracadaCoin */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 mb-5">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🪙</span>
                    <span className="text-[#1E3A5F] text-sm font-medium">{formula.abracaCoins.toLocaleString()} crédits</span>
                  </div>
                  <span className="text-gray-500 text-sm">/mois</span>
                </div>
                
                {/* CTA - Bleu navy pour Pro/Premium, Orange pour Business */}
                <button
                  className={`w-full py-3 rounded-xl font-semibold transition-all mb-5 ${
                    formula.popular
                      ? 'bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white hover:from-[#EA580C] hover:to-[#DC2626]'
                      : 'bg-[#1E3A5F] text-white hover:bg-[#2D4A6F]'
                  }`}
                >
                  Choisir cette formule
                </button>
                
                {/* Paramétrage clé en main */}
                <div className="p-3 rounded-lg bg-[#F97316]/10 border border-[#F97316]/20 mb-5">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#1E3A5F]">Paramétrage clé en main</span>
                    <div className="flex items-center gap-2">
                      {isYearly && (
                        <span className="text-sm text-gray-400 line-through">{formula.setupPrice}€</span>
                      )}
                      <span className="font-bold text-[#F97316]">
                        {isYearly ? formula.setupPriceFounder : formula.setupPrice}€
                      </span>
                    </div>
                  </div>
                  {isYearly && (
                    <p className="text-xs text-[#22C55E] mt-1">Économise {savingsSetup}€ sur le paramétrage</p>
                  )}
                </div>
                
                {/* Features */}
                <div className="border-t border-gray-200 pt-5">
                  <p className="text-gray-500 text-xs mb-3 uppercase tracking-wide">Inclut :</p>
                  <ul className="space-y-2.5">
                    {formula.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#22C55E] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
